"use client";

import React, { useState, useEffect } from "react";
import { updateSession, fetchSessionById } from "../../api/session.api";

const EditSessionModal = ({ sessionId, dome, onClose, onUpdated }) => {
  const [sessionData, setSessionData] = useState({
    name: "",
    startTime: "", // "10:45 AM" format for API
    duration: "",
  });
  const [inputTime, setInputTime] = useState(""); // HH:mm format for input

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadSession = async () => {
      try {
        const data = await fetchSessionById(sessionId);
        if (data) {
          let hhmm = "";
          if (data.startTime) {
            const [time, period] = data.startTime.split(" ");
            let [hour, minute] = time.split(":").map(Number);
            if (period === "PM" && hour < 12) hour += 12;
            if (period === "AM" && hour === 12) hour = 0;
            hhmm = `${String(hour).padStart(2, "0")}:${String(minute).padStart(
              2,
              "0"
            )}`;
          }

          setSessionData({
            name: data.name || "",
            startTime: data.startTime || "",
            duration: data.duration?.toString() || "",
          });
          setInputTime(hhmm);
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load session details");
      } finally {
        setLoading(false);
      }
    };
    loadSession();
  }, [sessionId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSessionData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTimeChange = (e) => {
    const value = e.target.value; // HH:mm
    setInputTime(value);

    // Convert to "10:45 AM" for API
    const [hour, minute] = value.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    const formattedHour = hour % 12 || 12;
    const formattedTime = `${formattedHour}:${String(minute).padStart(
      2,
      "0"
    )} ${period}`;

    setSessionData((prev) => ({ ...prev, startTime: formattedTime }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      await updateSession(sessionId, {
        name: sessionData.name,
        startTime: sessionData.startTime,
        duration: parseInt(sessionData.duration),
      });
      if (onUpdated) onUpdated();
      onClose();
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to update session");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return <div className="modal-body text-center">Loading session...</div>;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
      <div
        className="modal show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ zIndex: 1050 }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Edit Session for:{" "}
                <span className="text-primary">{dome?.domeName || "N/A"}</span>
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="mb-3">
                  <label className="form-label">Session Name</label>
                  <input
                    type="text"
                    name="name"
                    value={sessionData.name}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Start Time</label>
                    <input
                      type="time"
                      name="startTime"
                      value={inputTime}
                      onChange={handleTimeChange}
                      className="form-control"
                      required
                    />
                    {sessionData.startTime && (
                      <small className="text-muted">
                        Selected Time: {sessionData.startTime}
                      </small>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Duration (minutes)</label>
                    <input
                      type="number"
                      name="duration"
                      value={sessionData.duration}
                      onChange={handleChange}
                      className="form-control"
                      min={1}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? "Updating..." : "Update Session"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditSessionModal;
